import { Button, Grid } from "@material-ui/core";
import { HeaderTabProps } from "@saleor/changeOrderDomestic/components/HeaderTab";
import ModalAddress from "@saleor/changeOrderDomestic/components/ModalAddress";
import ModalCreditMaster from "@saleor/changeOrderDomestic/components/ModalCreditMaster";
import { ViewEditMode } from "@saleor/changeOrderExport/views/ViewEditOrder/types";
import DatePicker from "@saleor/components/DatePicker";
import MyTypography from "@saleor/components/MyTypography";
import { SingleSelectField } from "@saleor/components/SingleSelectField";
import { SingleTextField } from "@saleor/components/SingleTextField";
import {
  OrderType,
  RealtimePartnerType,
  useGet_Realtime_PartnerMutation,
  useGetCreditLimitLazyQuery,
  useGetIncotermQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import CreditCardIcon from "@saleor/icons/CreditCard";
import { makeStyles } from "@saleor/macaw-ui";
import { getRandomNumber } from "@saleor/utils/getRandowNumber";
import classNames from "classnames";
import { FormikProps } from "formik";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

// import { RealtimePartnerType } from "../../ModalAddress/types";

interface PartnerTabProps extends HeaderTabProps {
  formik: FormikProps<any>;
  customerGroup1: any[];
  customerGroup2: any[];
  customerGroup3: any[];
  customerGroup4: any[];
  setBillToInsteaded: (value: string) => void;
  setShipToInsteaded: (value: string) => void;
  soldToCode: string;
}

const useStyles = makeStyles(
  theme => ({
    rowFlex: {
      display: "flex",
      alignItems: "center"
    },
    flexRight: {
      marginLeft: "auto"
    },
    btnThin: {
      height: 32
    },
    boxBackground: {
      borderRadius: 4,
      background: theme.palette.background.default
    },
    boxSoldTo: {
      padding: 12,
      margin: "10px 0 24px 0"
    },
    colorInfo: {
      color: theme.palette.info.dark
    },
    boxCustomer: {
      padding: "16px 12px",
      margin: "10px 0 20px 0",
      "& .MuiTypography-root": {
        margin: "10px 0"
      },
      "& .MuiFormControl-root": {
        height: "4rem"
      }
    },
    boxGrid: {
      "& .MuiTypography-root": {
        margin: "10px 0"
      },
      "& .MuiFormControl-root": {
        height: "4rem"
      }
    },
    normalText: {
      fontWeight: "normal"
    },
    errorText: {
      color: theme.palette.error.light
    },
    rowTitle: {
      marginBottom: 10
    },
    rowContent: {
      marginBottom: 30
    },
    requiredMark: {
      color: theme.palette.error.main,
      fontWeight: 400
    }
  }),
  { name: "PartnerTab" }
);

const PartnerTab: React.FC<PartnerTabProps> = props => {
  const {
    formik,
    dataOrder,
    customerGroup1,
    customerGroup2,
    customerGroup3,
    customerGroup4,
    setBillToInsteaded,
    setShipToInsteaded,
    soldToCode
  } = props;
  const classes = useStyles();
  const intl = useIntl();
  const [{ viewEditMode }] = useAppState();

  const [openModalAddress, setOpenModalAddress] = useState<boolean>(false);
  const [typeAddress, setTypeAddress] = useState<RealtimePartnerType>(
    RealtimePartnerType.SHIP_TO
  );
  const [openModalCreditMaster, setOpenModalCreditMaster] = useState<boolean>(
    false
  );

  const [
    getRealtimePartnerData,
    { data: realtimePartnerData }
  ] = useGet_Realtime_PartnerMutation({
    variables: {
      type: [RealtimePartnerType.BILL_TO, RealtimePartnerType.SHIP_TO],
      saleOrgCode: dataOrder?.salesOrg,
      soldToCode,
      piMessageId: getRandomNumber(),
      distributionChannel: dataOrder?.distributionChannel,
      division: dataOrder?.division,
      orderType: OrderType.DOMESTIC
    }
  });

  useEffect(() => {
    if (dataOrder?.salesOrg) {
      getRealtimePartnerData();
    }
  }, [dataOrder]);

  const stringHyphenJoin = (_string1: string, _string2: string) => {
    if (_string1 === "") {
      return "";
    }
    if (_string2 === "") {
      return _string1;
    }
    return `${_string1} - ${_string2}`;
  };

  const getSoldToText = ({
    withSpace = false
  }: { withSpace?: boolean } = {}): string => {
    const agPartner = dataOrder?.orderPartners?.find(
      p => p?.partnerRole === "AG"
    );
    if (!agPartner) {
      return "";
    }
    const code = agPartner.partnerNo ?? "";
    const name = agPartner.address?.[0]?.name ?? "";
    return withSpace ? `${code} - ${name}` : `${code}-${name}`;
  };

  const [
    callCreditLimit,
    { data: dataCreditLimit, loading: loadingCreditLimit }
  ] = useGetCreditLimitLazyQuery({
    variables: {
      input: {
        contractNo: dataOrder?.contractNo,
        salesOrgCode: dataOrder?.salesOrg,
        soldToCode
      }
    },
    fetchPolicy: "no-cache"
  });

  // const handleOpenModalAddress = (type: RealtimePartnerType) => {
  const { data: dataIncoterm } = useGetIncotermQuery({
    fetchPolicy: "cache-first"
  });

  const handleOpenModalAddress = (type: RealtimePartnerType) => {
    setTypeAddress(type);
    // if (type === RealtimePartnerType.BILL_TO) {
    //   getBillToData();
    // }
    // if (type === RealtimePartnerType.SHIP_TO) {
    //   getShipToData();
    // }
    getRealtimePartnerData();
    setOpenModalAddress(true);
  };

  const handleCloseModalAddress = () => {
    setOpenModalAddress(false);
  };

  const handleApplyAddress = (address, type) => {
    if (type === RealtimePartnerType.SHIP_TO) {
      formik.setFieldValue("shipTo", address);
      setShipToInsteaded(address);
    } else if (type === RealtimePartnerType.BILL_TO) {
      formik.setFieldValue("billTo", address);
      setBillToInsteaded(address);
    }
  };

  const handleOpenCreditMaster = () => {
    callCreditLimit();
  };

  useEffect(() => {
    if (dataCreditLimit?.getCreditLimit) {
      setOpenModalCreditMaster(true);
    }
  }, [dataCreditLimit]);

  const mappedRealtimePartnerData = useMemo(
    () => realtimePartnerData?.getSapRealtimePartner?.response,
    [realtimePartnerData]
  );

  const getRealTimePartnerDataByType = () => {
    if (typeAddress === RealtimePartnerType.SHIP_TO) {
      return mappedRealtimePartnerData?.filter(e => e.partnerFunction === "WE");
    }
    if (typeAddress === RealtimePartnerType.BILL_TO) {
      return mappedRealtimePartnerData?.filter(e => e.partnerFunction === "RE");
    }
  };

  const options = useMemo(() => getRealTimePartnerDataByType(), [
    typeAddress,
    realtimePartnerData
  ]);

  const incotermChoices =
    useMemo(
      () =>
        dataIncoterm?.incoterms1?.map(item => ({
          value: item.code,
          label: `${item.code}, ${item.description}`
        })),
      [dataIncoterm]
    ) || [];

  const handlePODate = e => {
    formik.handleChange(e);
  };

  return (
    <>
      <Grid container spacing={6} data-testid="partnerTabDetail">
        <Grid item xs={12} md={6}>
          <div className={classes.rowFlex}>
            <MyTypography variant={"subtitle3"}>
              {intl.formatMessage({ defaultMessage: " Sold-to Party" })}
            </MyTypography>
            <Button
              id="openCreditMasterButton"
              data-testid="openCreditMasterButton"
              variant={"outlined"}
              color={"secondary"}
              className={classNames(classes.flexRight, classes.btnThin)}
              onClick={handleOpenCreditMaster}
              disabled={loadingCreditLimit}
              startIcon={
                <CreditCardIcon style={{ marginRight: 0, marginLeft: 4 }} />
              }
            >
              <MyTypography variant={"body3"}>
                {intl.formatMessage({ defaultMessage: "Credit Master" })}
              </MyTypography>
            </Button>
          </div>
          <div className={classNames(classes.boxBackground, classes.boxSoldTo)}>
            <MyTypography
              variant={"body1"}
              className={classes.colorInfo}
              data-testid="soldTo-text"
            >
              {/* {dataOrder?.contract?.customer?.displayText} */}
              {getSoldToText()}
            </MyTypography>
            <MyTypography variant={"body3"} data-testid="soldToAddress-text">
              {/* {dataOrder?.soldToAddress} */}
              {dataOrder?.orderPartners.map(orderPartner => {
                if (orderPartner.partnerRole === "AG") {
                  return `${orderPartner.address[0].street} ${
                    orderPartner.address[0].strSuppl1
                  } ${orderPartner.address[0].strSuppl2 || ""} ${orderPartner
                    .address[0].strSuppl3 || ""} ${orderPartner.address[0]
                    .location || ""} ${orderPartner.address[0].district} ${
                    orderPartner.address[0].city
                  } ${orderPartner.address[0].postCode}`;
                }
                return "";
              })}
            </MyTypography>
          </div>
          <MyTypography variant={"subtitle3"}>
            {intl.formatMessage({ defaultMessage: "Customer Group" })}
          </MyTypography>
          <MyTypography
            variant={"body1"}
            className={classes.colorInfo}
            data-testid="customerGroup-text"
          >
            {stringHyphenJoin(
              dataOrder?.customerGroup,
              dataOrder?.customerGroupName
            )}
          </MyTypography>
          <div
            className={classNames(classes.boxBackground, classes.boxCustomer)}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <MyTypography variant={"captionBold"}>
                  Customer Group 1
                </MyTypography>
                {viewEditMode === ViewEditMode.VIEW ? (
                  <MyTypography
                    variant={"body3"}
                    data-testid="customerGroup1-text"
                  >
                    {stringHyphenJoin(
                      dataOrder?.customerGroup1,
                      dataOrder?.customerGroup1Name
                    )}
                  </MyTypography>
                ) : (
                  <SingleSelectField
                    value={formik.values.customerGroup1}
                    name={"customerGroup1"}
                    id="customerGroup1"
                    testId="customerGroup1-select"
                    InputProps={{
                      inputProps: {
                        "data-testid": "customerGroup1-input"
                      }
                    }}
                    placeholder={"Select"}
                    choices={customerGroup1}
                    onChange={formik.handleChange}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography variant={"captionBold"}>
                  Customer Group 2
                </MyTypography>
                {viewEditMode === ViewEditMode.VIEW ? (
                  <MyTypography
                    variant={"body3"}
                    data-testid="customerGroup2-text"
                  >
                    {stringHyphenJoin(
                      dataOrder?.customerGroup2,
                      dataOrder?.customerGroup2Name
                    )}
                  </MyTypography>
                ) : (
                  <SingleSelectField
                    value={formik.values.customerGroup2}
                    name={"customerGroup2"}
                    id="customerGroup2"
                    testId="customerGroup2-select"
                    InputProps={{
                      inputProps: {
                        "data-testid": "customerGroup2-input"
                      }
                    }}
                    placeholder={"Select"}
                    choices={customerGroup2}
                    onChange={formik.handleChange}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography variant={"captionBold"}>
                  Customer Group 3
                </MyTypography>
                {viewEditMode === ViewEditMode.VIEW ? (
                  <MyTypography
                    variant={"body3"}
                    data-testid="customerGroup3-text"
                  >
                    {stringHyphenJoin(
                      dataOrder?.customerGroup3,
                      dataOrder?.customerGroup3Name
                    )}
                  </MyTypography>
                ) : (
                  <SingleSelectField
                    value={formik.values.customerGroup3}
                    placeholder={"Select"}
                    choices={customerGroup3}
                    name={"customerGroup3"}
                    id="customerGroup3"
                    testId="customerGroup3-select"
                    InputProps={{
                      inputProps: {
                        "data-testid": "customerGroup3-input"
                      }
                    }}
                    onChange={formik.handleChange}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography variant={"captionBold"}>
                  Customer Group 4
                </MyTypography>
                {viewEditMode === ViewEditMode.VIEW ? (
                  <MyTypography
                    variant={"body3"}
                    data-testid="customerGroup4-text"
                  >
                    {stringHyphenJoin(
                      dataOrder?.customerGroup4,
                      dataOrder?.customerGroup4Name
                    )}
                  </MyTypography>
                ) : (
                  <SingleSelectField
                    value={formik.values.customerGroup4}
                    name={"customerGroup4"}
                    id="customerGroup4"
                    testId="customerGroup4-select"
                    InputProps={{
                      inputProps: {
                        "data-testid": "customerGroup4-input"
                      }
                    }}
                    placeholder={"Select"}
                    choices={customerGroup4}
                    onChange={formik.handleChange}
                  />
                )}
              </Grid>
            </Grid>
          </div>
          <Grid container spacing={3} className={classes.boxGrid}>
            <Grid item xs={12} md={6}>
              <MyTypography variant={"subtitle3"}>PO Date</MyTypography>
              {viewEditMode === ViewEditMode.VIEW ? (
                <MyTypography variant={"body3"} data-testid="poDate-text">
                  {formik.values.poDate
                    ? moment(formik.values.poDate, "YYYY-MM-DD").format(
                        "DD/MM/YYYY"
                      )
                    : ""}
                </MyTypography>
              ) : (
                <DatePicker
                  value={formik.values.poDate}
                  onChange={handlePODate}
                  name={"poDate"}
                  id="poDate"
                  inputProps={{
                    "data-testid": "poDate-input"
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <MyTypography variant={"subtitle3"}>Request Date</MyTypography>
              <MyTypography variant={"body3"} data-testid="requestDate-text">
                {dataOrder?.requestDate ? dataOrder?.requestDate : ""}
              </MyTypography>
            </Grid>
            <Grid item xs={12} md={6}>
              <MyTypography variant={"subtitle3"}>
                PO No.
                {viewEditMode === ViewEditMode.EDIT ? (
                  <span className={classes.requiredMark}>*</span>
                ) : (
                  ""
                )}
              </MyTypography>
              {viewEditMode === ViewEditMode.VIEW ? (
                <MyTypography variant={"body3"} data-testid="poNo-text">
                  {dataOrder?.poNo}
                </MyTypography>
              ) : (
                <SingleTextField
                  value={formik.values.poNo}
                  onChange={formik.handleChange}
                  name={"poNo"}
                  id="poNo"
                  InputProps={{
                    inputProps: {
                      "data-testid": "poNo-input"
                    }
                  }}
                  error={!!formik.errors.poNo}
                  maxLengthProps={10}
                />
              )}
              {!!formik.errors.poNo && viewEditMode === ViewEditMode.EDIT ? (
                <MyTypography variant={"body3"} className={classes.errorText}>
                  {formik.errors.poNo}
                </MyTypography>
              ) : (
                ""
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <MyTypography variant={"subtitle3"}>Payment Term</MyTypography>
              <MyTypography variant={"body3"} data-testid="paymentTerm-text">
                {dataOrder?.paymentTerms}
              </MyTypography>
            </Grid>
            <Grid item xs={12} md={6}>
              <MyTypography variant={"subtitle3"}>D/P No.</MyTypography>
              <MyTypography variant={"body3"} data-testid="dpNo-text">
                {dataOrder?.dp?.map(dpNo => dpNo.dpNo).join(", ")}
              </MyTypography>
            </Grid>
            <Grid item xs={12} md={6}>
              <MyTypography variant={"subtitle3"}>
                Invoice No.{" "}
                <span className={classes.normalText}>(Billing No.)</span>
              </MyTypography>
              <MyTypography variant={"body3"} data-testid="invoiceNo-text">
                {dataOrder?.invoice
                  ?.map(_invoice => _invoice.invoiceNo)
                  .join(", ")}
              </MyTypography>
            </Grid>
            <Grid item xs={12} md={6}>
              <MyTypography variant={"subtitle3"}>Delivery Block</MyTypography>
              <MyTypography variant={"body3"} data-testid="deliveryBlock-text">
                {dataOrder?.deliveryBlock}
              </MyTypography>
            </Grid>
            <Grid item xs={12} md={6}>
              <MyTypography variant={"subtitle3"}>
                Incoterms
                {viewEditMode === ViewEditMode.EDIT ? (
                  <span className={classes.requiredMark}>*</span>
                ) : (
                  ""
                )}
              </MyTypography>
              {viewEditMode === ViewEditMode.VIEW ? (
                <MyTypography variant={"body3"} data-testid="incoterms1-text">
                  {`${dataOrder?.incoterms1} ${
                    dataOrder?.incoterms1Name
                      ? " " + dataOrder?.incoterms1Name
                      : ""
                  }`}
                </MyTypography>
              ) : (
                <SingleSelectField
                  placeholder={"Select"}
                  choices={incotermChoices}
                  value={formik.values.incoterm}
                  onChange={formik.handleChange}
                  name={"incoterm"}
                  id="incoterm"
                  testId="incoterm-select"
                  InputProps={{
                    inputProps: {
                      "data-testid": "incoterm-input"
                    }
                  }}
                  error={!!formik.errors.incoterm}
                />
              )}
              {!!formik.errors.incoterm &&
              viewEditMode === ViewEditMode.EDIT ? (
                <MyTypography variant={"body3"} className={classes.errorText}>
                  {formik.errors.incoterm}
                </MyTypography>
              ) : (
                ""
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <div className={classes.rowFlex}>
            <MyTypography variant={"subtitle3"} className={classes.rowTitle}>
              Ship-to Party
            </MyTypography>
            {viewEditMode === ViewEditMode.EDIT &&
            (mappedRealtimePartnerData?.filter(e => e.partnerFunction === "WE")
              ?.length || 0) > 1 ? (
              <Button
                variant={"outlined"}
                color={"secondary"}
                className={classNames(classes.flexRight, classes.btnThin)}
                onClick={() =>
                  handleOpenModalAddress(RealtimePartnerType.SHIP_TO)
                }
                name="shipToButton"
                id="shipToButton"
                data-testid="shipToButton"
              >
                <MyTypography variant={"body3"}>
                  {intl.formatMessage({ defaultMessage: "แก้ไข" })}
                </MyTypography>
              </Button>
            ) : (
              ""
            )}
          </div>
          <MyTypography
            style={{
              whiteSpace: "pre-line"
            }}
            variant={"body3"}
            className={classes.rowContent}
            data-testid="shipTo-text"
          >
            {formik.values.shipTo}
          </MyTypography>
          <div className={classes.rowFlex}>
            <MyTypography variant={"subtitle3"} className={classes.rowTitle}>
              Bill-to Party
            </MyTypography>
            {viewEditMode === ViewEditMode.EDIT &&
            mappedRealtimePartnerData?.filter(e => e.partnerFunction === "RE")
              ?.length > 1 ? (
              <Button
                variant={"outlined"}
                color={"secondary"}
                className={classNames(classes.flexRight, classes.btnThin)}
                onClick={() =>
                  handleOpenModalAddress(RealtimePartnerType.BILL_TO)
                }
                name="billToButton"
                id="billToButton"
                data-testid="billToButton"
              >
                <MyTypography variant={"body3"}>
                  {intl.formatMessage({ defaultMessage: "แก้ไข" })}
                </MyTypography>
              </Button>
            ) : (
              ""
            )}
          </div>
          <MyTypography
            style={{
              whiteSpace: "pre-line"
            }}
            variant={"body3"}
            className={classes.rowContent}
            data-testid="billTo-text"
          >
            {formik.values.billTo}
          </MyTypography>
        </Grid>
      </Grid>
      {openModalAddress && (
        <ModalAddress
          onClose={handleCloseModalAddress}
          visible={openModalAddress}
          type={typeAddress}
          options={options}
          handleApplyAddress={handleApplyAddress}
          formik={formik}
          partnerName=""
          partnerNo=""
        />
      )}
      <ModalCreditMaster
        title="Credit Master"
        dataModal={dataCreditLimit?.getCreditLimit}
        open={openModalCreditMaster}
        onClose={() => setOpenModalCreditMaster(false)}
        soldTo={getSoldToText({ withSpace: true })}
      />
    </>
  );
};

export default PartnerTab;
